import { emit, on, onPageScroll, pageScrollBy, storage, session, observeReveal } from '@lucid/core';
import { sectionRenderer } from '@theme/section-renderer';

const GRID_KEY = 'lucid:grid';

const densityOf = (root, scope) => root.getAttribute(`data-density-${scope}`);

/**
 * Results page behaviour shared by collection and search: filter drawer, sort, active filter chips, grid density,
 * "Mehr laden", the floating glass filter bar, editorial tiles at the grid edge, Back/Forward through filter states
 * and the collapsible SEO text. The section is re-rendered by
 * Horizon's facets (morph), so everything works through delegation and is re-synced after each morph.
 */
class LucidCollection extends HTMLElement {
  #offs = [];
  #frame = 0;
  #mutationFrame = 0;
  #loading = false;
  #layoutFrame = 0;
  #path = window.location.pathname;
  #search = window.location.search;
  #mutations = new MutationObserver((records) => {
    if (records.every((record) => record.target instanceof Element && record.target.closest('lucid-countdown, [aria-live]'))) return;
    if (this.classList.contains('is-loading') && records.some((record) => record.target instanceof Element && record.target.closest('.lucid-collection__inner'))) {
      this.#stopLoading();
    }
    window.cancelAnimationFrame(this.#mutationFrame);
    this.#mutationFrame = window.requestAnimationFrame(() => this.#syncAll());
  });

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#offs.push(onPageScroll(this.#onScroll));
    window.addEventListener('resize', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#queueLayout, { passive: true });
    window.addEventListener('popstate', this.#onPopState);
    this.#offs.push(on('filter:open', () => this.openFilters()));
    this.#mutations.observe(this, { childList: true, subtree: true });
    this.#syncAll();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    window.removeEventListener('resize', this.#onScroll);
    window.removeEventListener('resize', this.#queueLayout);
    window.removeEventListener('popstate', this.#onPopState);
    this.#offs.forEach((off) => off());
    this.#offs = [];
    this.#mutations.disconnect();
  }

  get facetsForm() {
    return this.querySelector('[data-lucid-facets]');
  }

  #syncAll() {
    this.#path = window.location.pathname;
    this.#search = window.location.search;
    this.#syncReveal();
    this.#syncDensity();
    this.#syncSizes();
    this.#layoutTiles();
    this.#syncSeo();
    this.#syncIntro();
    this.#onScroll();
  }

  /*
   * Back and Forward through filter and sort states: facets.js pushes one history entry per change but does not
   * listen for popstate, so the grid would keep showing the newer state. Re-render the section for the restored URL.
   */
  #onPopState = () => {
    if (window.location.pathname !== this.#path || window.location.search === this.#search) return;
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    this.#search = window.location.search;
    this.#startLoading();
    sectionRenderer.renderSection(sectionId).catch(() => window.location.reload());
  };

  /*
   * Editorial tiles: CSS pins them to the left or right edge and dense packing fills the cells beside them. When too
   * few products follow a tile to fill its rows (short filtered results, the end of a step), the tile is hidden so
   * the grid never shows a hole.
   */
  #queueLayout = () => {
    window.cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = window.requestAnimationFrame(() => this.#layoutTiles());
  };

  #layoutTiles() {
    const grid = this.querySelector('[data-lucid-grid]');
    if (!grid) return;
    const tiles = Array.from(grid.querySelectorAll(':scope > .lucid-tile'));
    if (!tiles.length) return;
    for (const tile of tiles) tile.classList.remove('is-dropped');
    const style = getComputedStyle(grid);
    const columns = style.gridTemplateColumns.split(' ').map(parseFloat).filter((width) => width > 0);
    if (columns.length < 2) return;
    const gap = parseFloat(style.columnGap) || 0;
    const box = grid.getBoundingClientRect();
    let left = box.left + (parseFloat(style.paddingLeft) || 0);
    const centres = columns.map((width) => {
      const centre = left + width / 2;
      left += width + gap;
      return centre;
    });
    for (let pass = 0; pass < tiles.length; pass += 1) {
      const holed = tiles.find((tile) => !tile.classList.contains('is-dropped') && this.#tileHasHole(grid, tile, centres));
      if (!holed) break;
      holed.classList.add('is-dropped');
    }
  }

  #tileHasHole(grid, tile, centres) {
    const area = tile.getBoundingClientRect();
    if (!area.height) return false;
    const rects = [];
    for (const item of grid.children) {
      if (item === tile) continue;
      const rect = item.getBoundingClientRect();
      if (rect.height && rect.bottom > area.top && rect.top < area.bottom) rects.push(rect);
    }
    for (const y of [area.top + area.height * 0.25, area.top + area.height * 0.75]) {
      for (const x of centres) {
        if (x >= area.left && x <= area.right) continue;
        if (!rects.some((rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom)) return true;
      }
    }
    return false;
  }

  /* Filter drawer: lucid-drawer.js opens it on lucid:filter:open; the plain dialog is the fallback. */

  openFilters() {
    if (customElements.get('lucid-drawer')) return;
    const dialog = this.querySelector('[data-lucid-filter-drawer] dialog');
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-lucid-filter-open]');
    if (opener) {
      event.preventDefault();
      emit('filter:open', {}, opener);
      return;
    }

    const closer = event.target.closest('[data-lucid-drawer-close]');
    if (closer && !customElements.get('lucid-drawer')) {
      closer.closest('dialog')?.close();
      return;
    }

    const remove = event.target.closest('a[data-lucid-filter-remove]');
    if (remove instanceof HTMLAnchorElement) {
      const form = this.facetsForm;
      if (form && typeof form.updateFiltersByURL === 'function') {
        event.preventDefault();
        this.#startLoading();
        form.updateFiltersByURL(remove.href);
      }
      return;
    }

    const density = event.target.closest('[data-density]');
    if (density instanceof HTMLElement) {
      event.preventDefault();
      this.#setDensity(density.dataset.densityScope, density.dataset.density);
      // The compact toolbar hides the pressed option, so focus moves to the one that is still shown.
      if (density === document.activeElement && density.offsetParent === null) {
        density.parentElement?.querySelector('[data-density]:not([aria-pressed="true"])')?.focus();
      }
      return;
    }

    const reveal = event.target.closest('[data-lucid-reveal-more]');
    if (reveal instanceof HTMLElement) {
      event.preventDefault();
      this.#reveal(true);
      return;
    }

    const more = event.target.closest('a[data-lucid-load-more]');
    if (more instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#loadPage(more, 'next');
      return;
    }

    const previous = event.target.closest('a[data-lucid-load-prev]');
    if (previous instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#loadPage(previous, 'previous');
      return;
    }

    const seo = event.target.closest('[data-lucid-seo-toggle]');
    if (seo instanceof HTMLElement) {
      event.preventDefault();
      this.#toggleSeo(seo);
      return;
    }

    const intro = event.target.closest('[data-lucid-intro-toggle]');
    if (intro instanceof HTMLElement) this.#toggleIntro(intro);
  };

  /* Sort: every visible select mirrors into the one sort_by field of the facets form. */

  #pendingTimer = 0;

  /* Results skeleton from the moment a filter or sort changes until the re-rendered grid arrives. */
  #loadingTimer = 0;

  #startLoading() {
    this.classList.add('is-loading');
    this.querySelector('[data-lucid-grid]')?.setAttribute('aria-busy', 'true');
    window.clearTimeout(this.#loadingTimer);
    this.#loadingTimer = window.setTimeout(() => this.#stopLoading(), 6000);
  }

  #stopLoading() {
    window.clearTimeout(this.#loadingTimer);
    this.classList.remove('is-loading');
    this.querySelector('[data-lucid-grid]')?.removeAttribute('aria-busy');
  }

  /* The facets re-render replaces the results button without aria-busy, which ends the pending state. */
  #markPending() {
    this.#startLoading();
    for (const button of this.querySelectorAll('[data-lucid-filter-close]')) button.setAttribute('aria-busy', 'true');
    window.clearTimeout(this.#pendingTimer);
    this.#pendingTimer = window.setTimeout(() => {
      for (const button of this.querySelectorAll('[data-lucid-filter-close]')) button.removeAttribute('aria-busy');
    }, 4000);
  }

  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.closest('[data-lucid-facets]')) {
      this.#markPending();
      return;
    }
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-lucid-sort]')) return;
    const value = select.value;

    for (const other of this.querySelectorAll('select[data-lucid-sort]')) {
      if (other !== select) other.value = value;
      const text = other.parentElement?.querySelector('[data-lucid-sort-text]');
      if (text && !other.hasAttribute('data-compact')) text.textContent = other.selectedOptions[0]?.textContent?.trim() || '';
    }

    const form = this.facetsForm;
    const input = form?.querySelector('[data-lucid-sort-input]');
    if (form && input instanceof HTMLInputElement && typeof form.updateFilters === 'function') {
      input.value = value;
      this.#startLoading();
      form.updateFilters();
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  /* Grid density: mobile 1 or 2 columns, desktop 3 or 4. */

  #setDensity(scope, value) {
    if (!['mobile', 'desktop'].includes(scope)) return;
    this.setAttribute(`data-density-${scope}`, value);
    const stored = storage.getJSON(GRID_KEY, {}) || {};
    stored[scope] = value;
    storage.setJSON(GRID_KEY, stored);
    this.#syncDensity();
    this.#syncSizes();
    this.#layoutTiles();
  }

  #syncDensity() {
    for (const button of this.querySelectorAll('[data-density]')) {
      const pressed = densityOf(this, button.dataset.densityScope) === button.dataset.density;
      if (button.getAttribute('aria-pressed') !== String(pressed)) button.setAttribute('aria-pressed', String(pressed));
    }
  }

  #syncSizes = () => {
    const desktop = densityOf(this, 'desktop') === '3' ? '33vw' : '25vw';
    const mobile = densityOf(this, 'mobile') === '1' ? '100vw' : '50vw';
    const sizes = `(min-width: 990px) ${desktop}, (min-width: 750px) 33vw, ${mobile}`;
    /* Zoomed packshots (lucid-card--zoom) render 1.4x larger, so they ask for a 1.4x file (same rule as lucid-card.liquid). */
    const zoomed = sizes.replace(/(\d+vw)/g, 'calc($1 * 1.4)');
    for (const image of this.querySelectorAll('.lucid-grid .lucid-card .lucid-media__img')) {
      const want = image.closest('.lucid-card--zoom .lucid-card__layer--product') ? zoomed : sizes;
      if (image.sizes !== want) image.sizes = want;
    }
  };

  /*
   * Mehr laden and Vorherige laden: fetch the real ?page= URL through the Section Rendering API, add its items in place
   * and keep the address bar on the furthest page loaded, so Back from a product returns to the same products.
   */

  async #loadPage(link, direction) {
    if (this.#loading) return;
    const grid = this.querySelector('[data-lucid-grid]');
    const sectionId = this.dataset.sectionId;
    if (!grid || !sectionId) {
      window.location.assign(link.href);
      return;
    }
    this.#loading = true;
    link.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = html.querySelector('[data-lucid-grid]');
      if (!nextGrid) throw new Error('empty');

      const known = new Set(Array.from(grid.querySelectorAll('[data-product-id]'), (item) => item.dataset.productId));
      const fragment = document.createDocumentFragment();
      let first = null;
      for (const item of Array.from(nextGrid.children)) {
        if (item.matches('[data-product-id]') ? known.has(item.dataset.productId) : direction === 'next') continue;
        const adopted = document.adoptNode(item);
        adopted.classList.add('lucid-grid__item--appended');
        fragment.append(adopted);
        if (item.matches('[data-product-id]')) first ??= adopted;
      }

      if (direction === 'previous') {
        const anchor = grid.firstElementChild;
        const before = anchor ? anchor.getBoundingClientRect().top : 0;
        grid.prepend(fragment);
        const holder = this.querySelector('[data-lucid-prev]');
        const nextHolder = html.querySelector('[data-lucid-prev]');
        if (holder && nextHolder) holder.replaceWith(document.adoptNode(nextHolder));
        else holder?.remove();
        if (anchor) pageScrollBy(anchor.getBoundingClientRect().top - before);
      } else {
        grid.append(fragment);
        const pagination = this.querySelector('[data-lucid-pagination]');
        const nextPagination = html.querySelector('[data-lucid-pagination]');
        if (pagination && nextPagination) pagination.replaceWith(document.adoptNode(nextPagination));
        else pagination?.remove();
        try {
          window.history.replaceState(window.history.state, '', link.href);
        } catch {
          /* history unavailable in sandboxed frames */
        }
      }

      observeReveal(grid);
      this.#syncSizes();
      this.#layoutTiles();
      first?.querySelector('.lucid-card__title-link')?.focus({ preventScroll: true });
    } catch {
      window.location.assign(link.href);
    } finally {
      this.#loading = false;
      link.removeAttribute('aria-busy');
    }
  }

  /*
   * Mehr laden when the whole result set is already on the page (sold out last across all results): reveal the next
   * step in place. The count is kept per URL for this tab, so Back from a product shows the same products again.
   */

  get #revealKey() {
    return `lucid:reveal:${window.location.pathname}${window.location.search}`;
  }

  #revealed() {
    return this.querySelectorAll('.lucid-grid__item--later.is-revealed[data-product-id]').length;
  }

  #syncReveal() {
    const nav = this.querySelector('[data-lucid-reveal]');
    if (!nav) return;
    const step = Number(nav.getAttribute('data-step')) || 24;
    const wanted = Number(session.get(this.#revealKey, 0)) || 0;
    if (wanted > step + this.#revealed()) this.#reveal(false, wanted - step - this.#revealed());
    else this.#updateReveal(nav, step);
  }

  #reveal(byUser, count) {
    const nav = this.querySelector('[data-lucid-reveal]');
    if (!nav) return;
    const step = Number(nav.getAttribute('data-step')) || 24;
    const later = Array.from(this.querySelectorAll('.lucid-grid__item--later:not(.is-revealed)'));
    let budget = count ?? step;
    let first = null;
    for (const item of later) {
      const isProduct = item.hasAttribute('data-product-id');
      if (isProduct && budget === 0) break;
      item.classList.add('is-revealed');
      if (byUser) item.classList.add('lucid-grid__item--appended');
      if (isProduct) {
        budget -= 1;
        first ??= item;
      }
    }
    const shown = this.#updateReveal(nav, step);
    session.set(this.#revealKey, String(shown));
    observeReveal(this.querySelector('[data-lucid-grid]') || this);
    this.#syncSizes();
    this.#layoutTiles();
    if (byUser) first?.querySelector('.lucid-card__title-link')?.focus({ preventScroll: true });
  }

  #updateReveal(nav, step) {
    const total = Number(nav.getAttribute('data-total')) || 0;
    const shown = Math.min(total, step + this.#revealed());
    const status = nav.querySelector('[data-lucid-reveal-status]');
    const text = (nav.getAttribute('data-template') || '').replace('[shown]', String(shown));
    if (status && text && status.textContent !== text) status.textContent = text;
    const bar = nav.querySelector('[data-lucid-reveal-bar]');
    if (bar && bar.getAttribute('aria-valuenow') !== String(shown)) {
      bar.setAttribute('aria-valuenow', String(shown));
      bar.querySelector('.lucid-pagination__fill')?.style.setProperty('--lc-progress', String(total ? shown / total : 1));
    }
    const button = nav.querySelector('[data-lucid-reveal-more]');
    if (button instanceof HTMLElement) button.hidden = shown >= total;
    return shown;
  }

  /* Floating filter bar: visible after the toolbar scrolled out of view, hidden near the end of the grid. */

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = window.requestAnimationFrame(() => {
      this.#frame = 0;
      const bar = this.querySelector('[data-lucid-floating-bar]');
      if (!bar) return;
      const toolbar = this.querySelector('[data-lucid-toolbar]');
      const end = this.querySelector('[data-lucid-grid-end]');
      const toolbarGone = toolbar ? toolbar.getBoundingClientRect().bottom < 0 : false;
      const nearEnd = end ? end.getBoundingClientRect().top < window.innerHeight - 24 : false;
      const drawerOpen = Boolean(this.querySelector('#filters-drawer[open]'));
      const visible = toolbarGone && !nearEnd && !drawerOpen;
      if (bar.classList.contains('is-visible') !== visible) {
        bar.classList.toggle('is-visible', visible);
        bar.toggleAttribute('inert', !visible);
        if (!visible && bar.contains(document.activeElement)) toolbar?.querySelector('button, select')?.focus({ preventScroll: true });
      }
    });
  };

  /* Intro, collapsed to its clamp height */

  #syncIntro() {
    const box = this.querySelector('[data-lucid-intro]');
    const toggle = box?.querySelector('[data-lucid-intro-toggle]');
    const text = box?.querySelector('.lucid-collection__intro-text');
    if (!toggle || !text || toggle.getAttribute('aria-expanded') === 'true') return;
    toggle.hidden = text.scrollHeight <= text.clientHeight + 2;
  }

  #toggleIntro(toggle) {
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    toggle.closest('[data-lucid-intro]')?.classList.toggle('is-open', open);
    toggle.querySelector('[data-more]').hidden = open;
    toggle.querySelector('[data-less]').hidden = !open;
  }

  /* SEO text, collapsed after six lines */

  #syncSeo() {
    const body = this.querySelector('[data-lucid-seo-body]');
    const toggle = this.querySelector('[data-lucid-seo-toggle]');
    if (!body || !toggle) return;
    if (toggle.getAttribute('aria-expanded') === 'true') return;
    body.classList.add('is-clamped');
    toggle.hidden = body.scrollHeight <= body.clientHeight + 2;
    if (toggle.hidden) body.classList.remove('is-clamped');
  }

  #toggleSeo(toggle) {
    const body = this.querySelector('[data-lucid-seo-body]');
    if (!body) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    body.classList.toggle('is-clamped', !open);
    const label = toggle.querySelector('.lucid-btn__label') || toggle;
    label.textContent = open ? toggle.dataset.labelLess : toggle.dataset.labelMore;
  }
}

if (!customElements.get('lucid-collection')) customElements.define('lucid-collection', LucidCollection);

